import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { makeApiRequests } from '../../helpers/api';

export default function HomeShortlists({ contactId, show, onHide }) {
  const [homes, setHomes] = useState([]);
  const [shortlists, setShortlists] = useState([]);
  const [selected, setSelected] = useState([]);
  const [message, setMessage] = useState('Here are the homes I would start with. Take a look with your family and tell me which feels closest.');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const base = `/marketing/contacts/${contactId}`;

  async function refresh() {
    const result = await makeApiRequests({ endpoint: base + '/shortlists', method: 'GET' });
    if (result.error) throw new Error(result.error);
    setShortlists(result.response.shortlists || []);
  }
  useEffect(() => {
    if (!show) return;
    let current = true;
    setLoading(true); setError(''); setNotice(''); setSelected([]); setHomes([]); setShortlists([]); setSearch('');
    Promise.all([
      makeApiRequests({ endpoint: base + '/homes', method: 'GET' }),
      makeApiRequests({ endpoint: base + '/shortlists', method: 'GET' })
    ]).then(([catalog, saved]) => {
      if (!current) return;
      if (catalog.error || saved.error) throw new Error(catalog.error || saved.error);
      if (!Array.isArray(catalog.response.models) || !Array.isArray(saved.response.shortlists)) throw new Error('Shortlist tools are not available on this CRM server yet.');
      setHomes(catalog.response.models); setShortlists(saved.response.shortlists);
    }).catch(e => { if (current) setError(e.message); }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [show, contactId]);
  const filtered = useMemo(() => homes.filter(h => `${h.name} ${h.manufacturer} ${h.category} ${h.beds} bedroom`.toLowerCase().includes(search.toLowerCase())).slice(0,60), [homes,search]);
  function select(slug) {
    setSelected(old => old.includes(slug) ? old.filter(s => s !== slug) : old.length < 3 ? [...old,slug] : old);
  }
  async function create() {
    setSaving(true); setError(''); setNotice('');
    const result = await makeApiRequests({endpoint:base+'/shortlists', requestBody:{homeSlugs:selected,message}});
    if(result.error) { setError(result.error); setSaving(false); return; }
    setSelected([]); setNotice('Your shortlist is ready. Copy the new link below and send it to your customer.');
    try { await refresh(); } catch(e) { setError(e.message); }
    setSaving(false);
  }
  async function revoke(id) {
    setSaving(true); setError('');
    const result=await makeApiRequests({endpoint:base+'/shortlists/'+id,method:'DELETE'});
    if(result.error) setError(result.error);
    else { try { await refresh(); setNotice('Link disabled. Existing responses are retained.'); } catch(e) { setError(e.message); } }
    setSaving(false);
  }
  async function copy(url) {
    try { await navigator.clipboard.writeText(url); setNotice('Link copied.'); }
    catch { setNotice('Select the link text to copy it manually.'); }
  }
  const homeName=slug=>homes.find(h=>h.slug===slug)?.name || slug;
  return <Modal show={show} onHide={onHide} size="xl" scrollable>
    <Modal.Header closeButton><Modal.Title>Send three homes</Modal.Title></Modal.Header>
    <Modal.Body>
      <p>Select one to three published models. Your customer gets one page with the homes, their assigned consultant, and a visit request. Responses stay on this CRM record.</p>
      {error && <Alert variant="danger" role="alert">{error}</Alert>}
      {notice && <Alert variant="success" role="status">{notice}</Alert>}
      {loading ? <Spinner animation="border" role="status"><span className="sr-only">Loading homes</span></Spinner> : <>
        <Form.Group controlId="shortlist-search"><Form.Label>Find a home</Form.Label><Form.Control value={search} onChange={e=>setSearch(e.target.value)} placeholder="Model, manufacturer, home type, or bedrooms" /></Form.Group>
        <p><strong>{selected.length} of 3 selected</strong>{selected.length>0 && ': '+selected.map(homeName).join(' · ')}</p>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))',gap:12,maxHeight:380,overflowY:'auto',padding:2}}>
          {filtered.map(home=><label key={home.slug} style={{border:selected.includes(home.slug)?'2px solid #8d0934':'1px solid #ddd',borderRadius:8,padding:12,margin:0,cursor:'pointer'}}>
            {home.image && <img src={home.image} alt="" loading="lazy" style={{width:'100%',height:120,objectFit:'cover',borderRadius:4}} />}
            <span style={{display:'block',marginTop:8}}><input type="checkbox" checked={selected.includes(home.slug)} disabled={saving || (!selected.includes(home.slug)&&selected.length===3)} onChange={()=>select(home.slug)} /> <strong>{home.name}</strong></span>
            <small>{home.manufacturer} · {home.beds} bed · {home.baths} bath · {home.sqft ? home.sqft.toLocaleString()+' sq ft':''}</small>
          </label>)}
        </div>
        {!filtered.length && <p>No published homes match that search.</p>}
        <Form.Group controlId="shortlist-message" className="mt-3"><Form.Label>Message shown on the shared page</Form.Label><Form.Control as="textarea" rows={2} maxLength={600} value={message} onChange={e=>setMessage(e.target.value)} /></Form.Group>
        <Button onClick={create} disabled={saving || !selected.length}> {saving?'Saving…':'Create shareable shortlist'}</Button>
        <p className="text-muted mt-2"><small>Links expire after 90 days and can be disabled below. A visit request becomes a follow-up task; you confirm the appointment with the customer.</small></p>
        <hr/><h5>Saved shortlists &amp; responses</h5>
        {!shortlists.length && <p>No shortlists yet.</p>}
        {shortlists.map(row=>{
          const inactive=!!row.revokedAt || new Date(row.expiresAt)<=new Date();
          return <section key={row.id} className="border rounded p-3 mb-3">
            <strong>{row.homeSlugs.map(homeName).join(' · ')}</strong>
            <p className="small mb-2">{inactive?'Inactive link':'Expires '+new Date(row.expiresAt).toLocaleDateString()} · {row.responses.length} response(s)</p>
            <Form.Control aria-label="Customer shortlist link" readOnly value={row.url} onFocus={e=>e.target.select()} />
            {!inactive && <div className="mt-2"><Button size="sm" onClick={()=>copy(row.url)}>Copy link</Button>{' '}<a className="btn btn-sm btn-outline-secondary" href={row.url} target="_blank" rel="noopener noreferrer">Preview</a>{' '}<Button size="sm" variant="outline-danger" disabled={saving} onClick={()=>revoke(row.id)}>Disable link</Button></div>}
            {row.responses.map(reply=><div key={reply.id} className="border-top mt-3 pt-2"><strong>Visit requested · {new Date(reply.createdAt).toLocaleString()}</strong><p className="mb-1">{reply.name} · {reply.phone}</p><p className="mb-1">Home: {homeName(reply.preferredHome)||'Discuss options'} · Preferred time: {reply.preferredTime||'Contact customer'}</p>{reply.message&&<p>{reply.message}</p>}<small>Email notification: {reply.notificationStatus}. Verify the supplied contact details before updating the customer record.</small></div>)}
          </section>;
        })}
      </>}
    </Modal.Body><Modal.Footer><Button variant="secondary" onClick={onHide}>Close</Button></Modal.Footer>
  </Modal>;
}
